export default {
  slug: "hyrox-open-session-vs-specialty-class",
  metaTitle: "HYROX Open Session vs Specialty Class: Which to Book",
  metaDescription:
    "Open sessions are free community HYROX workouts; specialty classes are 90-minute coached sessions. How the two differ in Dallas and which you should book first.",
  h1: "HYROX Open Session vs. Coached Specialty Class in Dallas: Which Should You Book?",
  category: "Classes & Booking",
  pillar: false,
  datePublished: "2026-10-06",
  dateModified: "2026-10-06",
  readTime: 7,
  hero: {
    src: "/dps/bg-stations.jpg",
    alt: "Athlete throwing a wall ball during a HYROX session in Dallas",
    caption: "Wall balls under fatigue, the station both formats train.",
  },
  summary:
    "An open session is a free community HYROX workout, like the 60-minute DPS Sunday session capped at 24 athletes. A specialty class is a 90-minute coached session ($20) that follows the 12-week program. Start with whichever gets you in the door, then run both in the same week.",
  takeaways: [
    "Open sessions: free, 60 minutes, community pace, reserve-a-spot format.",
    "Specialty classes: $20, 90 minutes, coached standards, periodized to the 12-week program.",
    "The formats answer different questions: \u201Cdo I like this?\u201D versus \u201Cam I getting better at this?\u201D",
    "Most consistent Dallas athletes run one class plus the Sunday session weekly.",
  ],
  body: [
    { t: "p", html: "Two doors into HYROX training in Dallas, two very different rooms behind them. One costs nothing and asks nothing. One costs $20 and holds you to race standards. Here's exactly how they differ and a straight answer on which to book first." },

    { t: "h2", id: "defined", text: "The two formats, defined" },
    { t: "p", html: '<strong>The open session</strong> is the <a href="/blog/dps-sunday-hyrox-workout">DPS Sunday HYROX workout</a>: a free 60-minute group session at Dallas Performance Systems, led by Coach Dez, capped at 24 athletes. Runs, sleds, carries and stations at a community pace. You reserve a spot and show up.' },
    { t: "p", html: '<strong>The specialty class</strong> is the coached 90-minute session (Fridays 6:00 PM, Saturdays 11:30 AM, $20) built on the <a href="/program">12-week program</a>: standards enforced, loads prescribed by stage, compromised running programmed on purpose. The full breakdown is in <a href="/blog/what-is-a-hyrox-class">what happens inside a HYROX class</a>.' },

    { t: "h2", id: "side-by-side", text: "Side by side" },
    {
      t: "table",
      head: ["", "Open session (DPS Sunday)", "Specialty class"],
      rows: [
        ["Cost", "Free", "$20 drop-in / $60 4-pack"],
        ["Length", "60 minutes", "90 minutes"],
        ["Coaching", "Led, community format", "Coached, standards enforced"],
        ["Programming", "Standalone workout", "Periodized, 12-week stages"],
        ["Capacity", "Capped at 24", "Class caps, book ahead"],
        ["Booking", "Free reservation on the event page", "Reserve via schedule/events"],
        ["Best for", "Trying it, community, extra volume", "Measurable race prep"],
      ],
    },

    { t: "h2", id: "open-right-call", text: "When the open session is the right call" },
    {
      t: "ul",
      items: [
        "<span>You've never touched a sled and want to feel the training before spending anything.</span>",
        "<span>You train solo all week and want one hard group hour.</span>",
        "<span>You're mid-program and Sunday is your extra volume day, not a skill day.</span>",
      ],
    },

    { t: "h2", id: "class-earns-it", text: "When the specialty class earns its $20" },
    {
      t: "ul",
      items: [
        "<span>You have a race or a <a href=\"/blog/what-is-a-hyrox-simulation\">simulation</a> on the calendar. Standards and pacing need coaching, not vibes.</span>",
        "<span>Your wall balls or sled pushes would get no-repped at a race and you know it.</span>",
        "<span>You want week-over-week progression instead of a random hard workout.</span>",
      ],
    },
    { t: "p", html: "The honest tell: if your goal has a date on it, you need the class. The open session makes you fitter; the class makes you race-ready." },

    { t: "h2", id: "combo", text: "The combo most Dallas athletes run" },
    { t: "p", html: "One specialty class (Friday or Saturday) plus the Sunday open session. That's one skill-and-standards day and one volume-and-community day, roughly $15 to 20 a week on a 4-pack. Add a home touch-session and you have a complete week." },
    { t: "disclosure" },
    { t: "p", html: 'For that third touch at home, the <a href="https://tidd.ly/4qjvvVA" rel="sponsored noopener" target="_blank">HYROX essentials bundle</a> covers the basics in one buy, or start with just a <a href="https://tidd.ly/4o0815U" rel="sponsored noopener" target="_blank">sled</a> if push/pull is your weak pair. Full list on the <a href="/gear">gear page</a>.' },

    { t: "h2", id: "book-each", text: "How to book each" },
    {
      t: "ol",
      items: [
        '<span><strong>Open session:</strong> reserve free on the <a href="/events">events page</a>. Spots cap at 24 and go fast; the waitlist emails you if one opens.</span>',
        '<span><strong>Specialty class:</strong> check times on the <a href="/schedule">schedule page</a> and reserve. Booking closes 2 hours before class.</span>',
      ],
    },
  ],
  faqs: [
    { q: "Is the DPS Sunday session really free?", a: "Yes. Reserving a spot costs nothing; the cap of 24 just keeps the floor workable. Registration confirms instantly on the event page." },
    { q: "Can a beginner jump straight into the open session?", a: "Yes, it's the most beginner-friendly entry point. Every movement scales and nobody is checking your pace." },
    { q: "Do open sessions follow the 12-week program?", a: "No. Sundays are standalone workouts. The specialty classes carry the periodization; Sundays add volume and community on top." },
    { q: "Which should I do before my first simulation?", a: "At least a few specialty classes. The sim holds official standards, and the class is where those standards get coached into you." },
  ],
  sources: [
    "Hyrox Ninja schedule and class pricing — hyroxninja.com/schedule",
    "DPS Sunday workout event page — hyroxninja.com/events",
  ],
  related: ["hyrox-classes-near-me-dallas", "what-is-a-hyrox-class", "dps-sunday-hyrox-workout"],
  cta: {
    heading: "Book one of each this week",
    html: "Reserve the free Sunday session, book one $20 class, and compare them yourself by Monday.",
    to: "/events",
    label: "See Open Registrations",
  },
};
